import React from 'react';
import { ArrowDown, CalendarOff, CheckCircle2, Eye, EyeOff, TrendingUp } from 'lucide-react';
import { cn } from '../../../utils/cn';
import PnlFlash from '../../common/PnlFlash';
import Tooltip from '../../ui/Tooltip';
import { useHidePnl } from '../../../hooks/useHidePnl';
import EmptyStateWithRedirect from '../../common/EmptyStateWithRedirect';
import { pickScheduleEmpty } from '../../../utils/emptyStates';
import type { ScheduleSummaryTotals } from '../../../types/schedule';

interface ProHeroBannerProps {
  totals: ScheduleSummaryTotals;
  selectedDate: string;        // "YYYY-MM-DD"
  isToday: boolean;
  /** Does the underlying DAY have any strategies (ignoring client-side
   *  filters)? When false → compact "No activity" state. When true, we
   *  always render the full layout even if the filtered totals are zero,
   *  so filter-driven empties don't collapse the hero and cause layout
   *  shift that repositions the table header beneath it. */
  dayHasActivity?: boolean;
}

function formatLongDate(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString('en-IN', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/**
 * Hero section of the Pro view — answers the trader's top 2 questions in
 * one glance:
 *   1. Am I up or down today?             (big P&L, colour-coded)
 *   2. What's the overall activity shape? (compact position stats —
 *      Open / Closed / Filled / Scheduled / Rejected / SL placed /
 *      SL pending / SL failed). A red border on the wrapper is the
 *      subtle "something needs attention" signal; the loud red
 *      "Jump to first" callout was retired once the AnomalyBanner
 *      (commit eb74563) added clickable filter chips above the table.
 *
 * Replaces the 7-card grid for the Pro view.
 */
const ProHeroBanner: React.FC<ProHeroBannerProps> = ({
  totals, selectedDate, isToday, dayHasActivity,
}) => {
  const [pnlHidden, setPnlHidden] = useHidePnl();
  // Prefer the explicit flag from the caller (source-of-truth = unfiltered
  // day totals). Fall back to the filtered totals for legacy callers that
  // don't know about the distinction.
  const hasActivity = dayHasActivity ?? totals.leg_count > 0;
  if (!hasActivity) {
    // Empty-day branch — show a context-aware CTA so the user lands
    // on the right next-step (today: create strategies; past: just
    // informational). Date strip stays for orientation.
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900">
        <div className="px-6 pt-6 text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {isToday ? 'Today' : formatLongDate(selectedDate)}
        </div>
        <EmptyStateWithRedirect
          size="card"
          icon={<CalendarOff className="w-8 h-8" />}
          {...pickScheduleEmpty({ isToday, isPast: !isToday })}
        />
      </div>
    );
  }

  const pnlPositive = totals.total_pnl >= 0;
  const hasIssues = (totals.rejected + totals.sl_rejected) > 0;

  return (
    <div
      className={cn(
        'relative rounded-xl border p-6 overflow-hidden',
        'bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900',
        hasIssues
          ? 'border-red-200 dark:border-red-900/50'
          : 'border-gray-200 dark:border-gray-700',
      )}
    >
      {/* Top strip: date + (subtle) refresh ping */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] uppercase tracking-widest font-medium text-gray-500 dark:text-gray-400">
          {isToday ? 'Today · ' : ''}
          {formatLongDate(selectedDate)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10 items-start">
        {/* Hero P&L column */}
        <div>
          <div className="flex items-baseline gap-2">
            <TrendingUp
              className={cn(
                'w-5 h-5',
                pnlHidden ? 'text-gray-400' : pnlPositive ? 'text-green-500' : 'text-red-500',
                !pnlHidden && !pnlPositive && 'rotate-180',
              )}
            />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-gray-500 dark:text-gray-400">
              Total P&amp;L
            </span>
            <Tooltip text={pnlHidden ? 'Show P&L' : 'Hide P&L'} position="top">
              <button
                type="button"
                onClick={() => setPnlHidden(!pnlHidden)}
                aria-label={pnlHidden ? 'Show P&L' : 'Hide P&L'}
                className="self-center p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                {pnlHidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
            </Tooltip>
          </div>
          <div className="mt-1 leading-tight">
            <PnlFlash
              value={totals.total_pnl}
              className="text-4xl md:text-5xl font-bold"
            />
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-600 dark:text-gray-300 flex-wrap">
            <span className="tabular-nums">
              <CheckCircle2 className="inline w-3.5 h-3.5 mr-0.5 text-green-500/80" />
              Realized <PnlFlash value={totals.realized_pnl} className="font-semibold" />
            </span>
            <span className="text-gray-300 dark:text-gray-600">·</span>
            <span className="tabular-nums">
              <ArrowDown className="inline w-3.5 h-3.5 mr-0.5 text-blue-500/70" />
              Unrealized <PnlFlash value={totals.unrealized_pnl} className="font-semibold" />
            </span>
          </div>
        </div>

        {/* Status column */}
        <div className="border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 md:pl-10 pt-4 md:pt-0">
          {/* Compact activity stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <Stat label="Open" value={totals.open_count} emphasis="blue" />
            <Stat label="Closed" value={totals.closed_count} />
            <Stat label="Filled" value={totals.filled} emphasis="green" />
            <Stat label="Scheduled" value={totals.leg_count} muted />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mt-3">
            <Stat
              label="Rejected"
              value={totals.rejected}
              emphasis={totals.rejected > 0 ? 'red' : 'muted'}
              sublabel={totals.cancelled > 0
                ? `${totals.cancelled} cancelled`
                : undefined}
            />
            <Stat
              label="SL placed"
              value={`${totals.sl_placed}`}
              emphasis={totals.sl_placed > 0 ? 'green' : 'muted'}
            />
            <Stat
              label="SL pending"
              value={totals.sl_pending}
              emphasis={totals.sl_pending > 0 ? 'yellow' : 'muted'}
            />
            <Stat
              label="SL failed"
              value={totals.sl_rejected}
              emphasis={totals.sl_rejected > 0 ? 'red' : 'muted'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const EMPHASIS_CLS: Record<string, string> = {
  green: 'text-green-600 dark:text-green-400',
  red:   'text-red-500 dark:text-red-400',
  blue:  'text-blue-600 dark:text-blue-400',
  yellow:'text-yellow-600 dark:text-yellow-400',
  muted: 'text-gray-400 dark:text-gray-500',
};

const Stat: React.FC<{
  label: string;
  value: number | string;
  emphasis?: keyof typeof EMPHASIS_CLS;
  muted?: boolean;
  /** Optional secondary line — small gray text under the value. Used to
   * surface a related informational count (e.g. cancelled next to rejected)
   * without adding a dedicated grid cell that would break row alignment. */
  sublabel?: string;
}> = ({ label, value, emphasis, muted, sublabel }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</div>
    <div
      className={cn(
        'text-lg font-bold tabular-nums mt-0.5',
        muted
          ? 'text-gray-400 dark:text-gray-500'
          : emphasis
            ? EMPHASIS_CLS[emphasis]
            : 'text-gray-900 dark:text-white',
      )}
    >
      {value}
    </div>
    {sublabel && (
      <div className="text-[10px] text-gray-500 dark:text-gray-400 tabular-nums mt-0.5 truncate">
        {sublabel}
      </div>
    )}
  </div>
);

export default ProHeroBanner;
